import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Typography,
  InputNumber,
  Switch,
  Row,
  Col,
  Button,
  Space,
  Spin,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  approvalService,
  type ApprovalWorkflowStepDTO,
} from '@/services/approval.service';
import { roleService } from '@/services/role.service';
import { userService } from '@/services/user.service';
import { departmentService } from '@/services/department.service';
import {
  APPROVAL_WORKFLOW_ENTITY_TYPE_VALUES,
  approvalWorkflowEntityTypeDisplay,
} from '@/constants/approval-entity-types';
import { useAuthStore } from '@/store/authStore';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

function userLabel(u: { first_name: string; last_name?: string; email: string; username: string }): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.email})` : `${u.username} (${u.email})`;
}

/**
 * Full-page create/edit for approval workflows.
 * Routes: `/platform/workflows/approvals/new`, `/platform/workflows/approvals/:id/edit`
 */
export const ApprovalWorkflowFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [entityTypeSearch, setEntityTypeSearch] = useState('');

  const isNew = location.pathname.endsWith('/new');
  const editingId = isNew ? null : params.id ?? null;

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['approval-definition', editingId],
    queryFn: () => approvalService.getDefinition(editingId!),
    enabled: !!editingId,
  });

  const authCompanyId = useAuthStore((s) => s.user?.company_id);
  const { data: roles = [] } = useQuery({
    queryKey: ['roles-for-approval-workflow', authCompanyId],
    queryFn: () =>
      roleService.getRoles({
        is_active: true,
        company_id: authCompanyId || undefined,
      }),
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users-for-approval-workflow'],
    queryFn: () => userService.getAll({ is_active: true }),
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['departments-for-approval-workflow'],
    queryFn: () => departmentService.getForSelection(),
  });

  const entityTypeSelectValues = useMemo(() => {
    const known = new Set(APPROVAL_WORKFLOW_ENTITY_TYPE_VALUES);
    const extras: string[] = [];
    if (existing?.entity_type && !known.has(existing.entity_type)) {
      extras.push(existing.entity_type);
    }
    const typed = entityTypeSearch.trim();
    if (typed && !known.has(typed) && !extras.includes(typed)) {
      extras.push(typed);
    }
    return [...APPROVAL_WORKFLOW_ENTITY_TYPE_VALUES, ...extras];
  }, [existing?.entity_type, entityTypeSearch]);

  useEffect(() => {
    if (isNew) {
      form.resetFields();
      form.setFieldsValue({
        trigger_mode: 'on_submit',
        is_active: true,
        is_amount_based: false,
        steps: [{ step_number: 1, approver_type: 'role' }],
      });
      return;
    }
    if (existing) {
      form.setFieldsValue({
        workflow_code: existing.workflow_code,
        workflow_name: existing.workflow_name,
        module: existing.module,
        entity_type: existing.entity_type,
        description: existing.description,
        trigger_mode: existing.trigger_mode,
        is_active: existing.is_active,
        is_amount_based: existing.amount_min != null || existing.amount_max != null,
        amount_min: existing.amount_min,
        amount_max: existing.amount_max,
        steps: (existing.steps || []).length
          ? existing.steps
          : [{ step_number: 1, approver_type: 'role' }],
      });
    }
  }, [isNew, existing, form]);

  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const steps = (values.steps as ApprovalWorkflowStepDTO[]) || [];
      const payload = {
        workflow_code: values.workflow_code as string,
        workflow_name: values.workflow_name as string,
        module: values.module as string | undefined,
        entity_type: values.entity_type as string,
        description: values.description as string | undefined,
        trigger_mode: (values.trigger_mode as string) || 'on_submit',
        amount_min: values.is_amount_based ? (values.amount_min as number) : null,
        amount_max: values.is_amount_based ? (values.amount_max as number) : null,
        is_active: values.is_active !== false,
        steps: steps.map((s, i) => ({
          step_number: s.step_number ?? i + 1,
          level_name: s.level_name,
          print_role_label: String(s.print_role_label ?? '').trim() || null,
          approver_type: s.approver_type || 'role',
          approver_role_id: s.approver_type === 'role' ? s.approver_role_id || undefined : undefined,
          approver_user_id: s.approver_type === 'user' ? s.approver_user_id || undefined : undefined,
          department_source:
            s.approver_type === 'department_process_owner' ? s.department_source || undefined : undefined,
          department_id:
            s.approver_type === 'department_process_owner' && s.department_source === 'fixed'
              ? s.department_id || undefined
              : undefined,
          is_optional: !!s.is_optional,
          is_parallel: !!s.is_parallel,
          escalation_hours: s.escalation_hours ?? 24,
        })),
      };
      if (editingId) {
        return approvalService.updateDefinition(editingId, payload);
      }
      return approvalService.createDefinition(payload as Parameters<typeof approvalService.createDefinition>[0]);
    },
    onSuccess: () => {
      message.success('Workflow saved');
      queryClient.invalidateQueries({ queryKey: ['approval-definitions'] });
      navigate('/platform/workflows/approvals');
    },
    onError: (e: any) => {
      const backendMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        (Array.isArray(e?.response?.data?.errors) ? e.response.data.errors[0] : undefined);
      message.error(backendMsg || e?.message || 'Save failed');
    },
  });

  if (!isNew && editingId && loadingExisting && !existing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 960 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/platform/workflows/approvals')}>
            Back to list
          </Button>
        </Space>

        <Title level={2}>{isNew ? 'Create workflow' : 'Edit workflow'}</Title>
        <Paragraph type="secondary">
          Configure steps and approvers. Pick the <Text code>entity_type</Text> that matches the document (e.g.{' '}
          <Text code>purchase_order</Text>, <Text code>production_request</Text>,{' '}
          <Text code>management_of_change</Text> (Management of Change), <Text code>controlled_document</Text>{' '}
          (Document Control), <Text code>goods_receipt_note</Text>, <Text code>supplier_invoice</Text>).
        </Paragraph>

        <Card>
          <Form form={form} layout="vertical" onFinish={(v) => saveMutation.mutate(v)}>
            {!editingId && (
              <Form.Item name="workflow_code" label="Workflow code" rules={[{ required: true }]}>
                <Input placeholder="e.g. PO_STANDARD" />
              </Form.Item>
            )}
            {editingId && (
              <Form.Item label="Workflow code">
                <Text strong>{existing?.workflow_code}</Text>
              </Form.Item>
            )}
            <Form.Item name="workflow_name" label="Workflow name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item
              name="entity_type"
              label="Entity type"
              rules={[{ required: true }]}
              extra="Type to search. You can also enter a custom code (e.g. production_request) if it is not listed yet."
            >
              <Select
                placeholder="Target document"
                showSearch
                optionFilterProp="children"
                onSearch={setEntityTypeSearch}
                onBlur={() => setEntityTypeSearch('')}
              >
                {entityTypeSelectValues.map((v) => (
                  <Option key={v} value={v}>
                    {approvalWorkflowEntityTypeDisplay(v)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="module" label="Module (optional)">
              <Input />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue>
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="is_amount_based" label="Amount-based band" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item noStyle shouldUpdate={(p, c) => p.is_amount_based !== c.is_amount_based}>
              {({ getFieldValue }) =>
                getFieldValue('is_amount_based') ? (
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="amount_min" label="Min amount">
                        <InputNumber style={{ width: '100%' }} min={0} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="amount_max" label="Max amount">
                        <InputNumber style={{ width: '100%' }} min={0} />
                      </Form.Item>
                    </Col>
                  </Row>
                ) : null
              }
            </Form.Item>

            <Title level={4} style={{ marginTop: 8 }}>
              Steps
            </Title>
            <Form.List name="steps">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <Card key={field.key} size="small" style={{ marginBottom: 12 }} title={`Step ${index + 1}`}>
                      <Form.Item name={[field.name, 'step_number']} label="Step #" initialValue={index + 1}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'level_name']} label="Level name">
                        <Input placeholder="e.g. Manager approval" />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'print_role_label']}
                        label="Print signature label"
                        tooltip="Shown above the signature on document PDFs. Leave blank for the default APPROVED BY."
                      >
                        <Input placeholder="Approved by" maxLength={100} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'approver_type']} label="Approver type" initialValue="role">
                        <Select
                          onChange={() => {
                            form.setFieldValue(['steps', field.name, 'approver_role_id'], undefined);
                            form.setFieldValue(['steps', field.name, 'approver_user_id'], undefined);
                            form.setFieldValue(['steps', field.name, 'department_source'], undefined);
                            form.setFieldValue(['steps', field.name, 'department_id'], undefined);
                          }}
                        >
                          <Option value="role">Role</Option>
                          <Option value="user">Specific user</Option>
                          <Option value="department_process_owner">Department Process Owner</Option>
                        </Select>
                      </Form.Item>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) =>
                          prev?.steps?.[field.name]?.approver_type !== cur?.steps?.[field.name]?.approver_type ||
                          prev?.steps?.[field.name]?.department_source !==
                            cur?.steps?.[field.name]?.department_source
                        }
                      >
                        {() => {
                          const approverType = form.getFieldValue(['steps', field.name, 'approver_type']);
                          if (approverType === 'user') {
                            return (
                              <Form.Item
                                name={[field.name, 'approver_user_id']}
                                label="Approver user"
                                rules={[{ required: true, message: 'Select a user' }]}
                              >
                                <Select
                                  showSearch
                                  allowClear
                                  placeholder="Select user"
                                  optionFilterProp="label"
                                  options={users.map((u) => ({
                                    value: u.id,
                                    label: userLabel(u),
                                  }))}
                                />
                              </Form.Item>
                            );
                          }
                          if (approverType === 'department_process_owner') {
                            const deptSource = form.getFieldValue(['steps', field.name, 'department_source']);
                            return (
                              <>
                                <Form.Item
                                  name={[field.name, 'department_source']}
                                  label="Department for Process Owner"
                                  rules={[{ required: true, message: 'Choose department source' }]}
                                  extra="Resolve the department's Process Owner (department head) as the approver."
                                >
                                  <Select
                                    placeholder="Choose source"
                                    onChange={() =>
                                      form.setFieldValue(['steps', field.name, 'department_id'], undefined)
                                    }
                                    options={[
                                      { value: 'document', label: 'From the document' },
                                      { value: 'fixed', label: 'Selected department on this workflow' },
                                    ]}
                                  />
                                </Form.Item>
                                {deptSource === 'fixed' ? (
                                  <Form.Item
                                    name={[field.name, 'department_id']}
                                    label="Department"
                                    rules={[{ required: true, message: 'Select a department' }]}
                                  >
                                    <Select
                                      showSearch
                                      allowClear
                                      placeholder="Select department"
                                      optionFilterProp="label"
                                      options={departments.map((d) => ({
                                        value: d.id,
                                        label: `${d.department_code} — ${d.department_name}`,
                                      }))}
                                    />
                                  </Form.Item>
                                ) : null}
                              </>
                            );
                          }
                          return (
                            <Form.Item
                              name={[field.name, 'approver_role_id']}
                              label="Approver role"
                              rules={[{ required: true, message: 'Select a role' }]}
                            >
                              <Select
                                showSearch
                                allowClear
                                placeholder="Select role"
                                optionFilterProp="label"
                                options={roles.map((r) => ({
                                  value: r.id,
                                  label: `${r.name} (${r.code})`,
                                }))}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                      <Button type="link" danger onClick={() => remove(field.name)}>
                        Remove step
                      </Button>
                    </Card>
                  ))}
                  <Button type="dashed" block onClick={() => add({ step_number: fields.length + 1, approver_type: 'role' })}>
                    Add step
                  </Button>
                </>
              )}
            </Form.List>

            <Form.Item style={{ marginTop: 24 }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={saveMutation.isPending} size="large">
                  Save workflow
                </Button>
                <Button size="large" onClick={() => navigate('/platform/workflows/approvals')}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Space>
    </div>
  );
};
